import { h as __h } from "preact";
import { Component, h } from "preact";
/** @import { ComponentChildren, RefObject } from "preact"; */

import { identity } from "~/lib/identity.js";
import html from "~/util/html.js";
import { pluralize } from "~/util/pluralize.js";
/** @import { paginatedResponse, query } from "~/lib/api.js"; */

/** @import { DropdownItem } from "~/component/Dropdown.js"; */
import Data from "~/component/Data.js";

/** @import Pagination from "~/admin/component/Pagination.js"; */
/** @import { TableHeader } from "~/admin/component/Table.js"; */
import Table from "~/admin/component/Table.js";
import { deleteGroup } from "./actions/deleteGroup.js";
import { renameGroup } from "./actions/renameGroup.js";
import adminStyle from "~/admin/lib/style.js";
import util from "~/style/util.js";
/** @import { groupResponse } from "~/backend/validate.js"; */

/**
 * @extends {Component<GroupTableProps>}
 *
 * @typedef {object} GroupTableProps
 * @property {string} orgId
 * @property {ReturnType<
 * 	typeof query<`/v2/orgs/${string}/groups`, ReturnType<typeof paginatedResponse<groupResponse>>>
 * >} query
 * @property {ComponentChildren} [footer]
 * @property {(h: TableHeader[]) => TableHeader[]} [headers]
 * @property {(
 * 	items: DropdownItem[],
 * 	group: groupResponse,
 * 	anchor: RefObject<HTMLButtonElement>,
 * ) => DropdownItem[]} [onTripleDots]
 * @property {InstanceType<typeof Pagination.State>} [paginate]
 * @property {InstanceType<typeof Table.Sort>} [sort]
 * @property {false} [scroll]
 */
export default class GroupTable extends Component {
  get #headers() {
    const h = [{
      id: "name",
      label: "Group",
      sort: "name"
    }, {
      id: "members",
      label: "Members"
    }, {
      id: "computers",
      label: "Computers"
    }];
    if (this.props.headers) return this.props.headers(h);
    return h;
  }

  /**
   * @returns {(
   * 	group: groupResponse,
   * 	anchor: { current: HTMLButtonElement },
   * ) => DropdownItem[]}
   */
  get #onTripleDots() {
    const canEdit = identity.permission(this.props.orgId, "groups", "edit");
    return (group, anchor) => {
      /** @type {DropdownItem[]} */
      const items = [];
      if (canEdit) {
        items.push({
          icon: "pencil",
          text: "Rename group",
          onSelect: () => renameGroup({
            orgId: this.props.orgId
          }, group)
        }, {
          icon: "trash",
          text: "Delete group",
          kind: "negative",
          onSelect: () => deleteGroup({
            orgId: this.props.orgId
          }, group)
        });
      }
      if (this.props.onTripleDots) return this.props.onTripleDots(items, group, anchor);
      return items;
    };
  }
  render() {
    return h(Data, {
      loading: this.props.query.loading && !this.props.query.data,
      error: this.props.query.error,
      children: h(/** @type {typeof Table<groupResponse>} */Table, {
        label: "group",
        headers: this.#headers,
        empty: "No groups found.",
        footer: this.props.footer,
        rows: this.props.query.data?.data.map(group => {
          return {
            key: group.id,
            label: group.name,
            value: group,
            columns: {
              name: GroupTable.primary(group),
              members: GroupTable.members(group),
              computers: GroupTable.computers(group)
            }
          };
        }) ?? [],
        paginate: this.props.paginate?.props(this.props.query),
        sort: this.props.sort?.props(),
        href: group => `/org/${this.props.orgId}/groups/${group.id}`,
        onTripleDots: this.#onTripleDots,
        scroll: this.props.scroll
      })
    });
  }

  /** @param {groupResponse} group */
  static primary(group) {
    return __h("div", {
      "class": adminStyle.withDesc
    }, __h("span", {
      "class": util.truncate
    }, group.name), __h("span", {
      "class": util.truncate + " " + adminStyle.desc
    }, group.description));
  }

  /** @param {groupResponse} group */
  static members(group) {
    return `${group.member_count} ${pluralize(group.member_count, "member")}`;
  }

  /** @param {groupResponse} group */
  static computers(group) {
    return `${group.machine_count} ${pluralize(group.machine_count, "computer")}`;
  }
}